import { toUid } from "@/helpers/utility/Id";
import { useFeatures } from "@editor/store/features.store";
import { useSettingsStore } from "@editor/store/settings.store";

import type {
  ConditionActivatorType,
  ConditionComparedType,
  ConditionItem,
  ConditionItemProperty,
  ConditionOperator,
  ConditionTypeId
} from "./ConditionItem";
import type { SimulationRoundItem } from "../simulationRound/SimulationRoundItem";

/**
 * Check if a condition applies in a specific round
 */
export function isConditionFulfilled(
  condition: ConditionItem,
  rounds: SimulationRoundItem[]
) {
  const activatorValue = getConditionActivatorValue(condition, rounds);
  const comparedValue = getConditionComparedValue(condition, rounds);

  const comparisonTrue = compareConditionValues(
    activatorValue,
    comparedValue,
    condition.operator
  );

  // console.log(
  //   "test",
  //   condition.activatorType,
  //   condition.activatorProperty,
  //   activatorValue,
  //   condition.operator,
  //   comparedValue,
  //   comparisonTrue
  // );

  return comparisonTrue;
}

export function compareConditionValues(
  activatorValue: number | undefined,
  comparedValue: number | undefined,
  operator: ConditionItem["operator"]
) {
  if (activatorValue == null) {
    switch (operator) {
      case "?":
        return false;
      case "!":
        return true;
      case "=":
        return comparedValue == null;
      case "!=":
        return comparedValue != null;
      default:
        console.warn(
          "activatorValue is null",
          activatorValue,
          operator,
          comparedValue
        );
        return false;
    }
  }

  // Check for existence first.
  if (operator == "?" || operator == "!") {
    switch (operator) {
      case "?":
        if (activatorValue == null) return false;
        return activatorValue != 0;
  
      case "!":
        if (activatorValue == null) return false;
        return activatorValue == 0;
    }
  }

  if (comparedValue == null) {
    console.log("comparedValue is null");
    return false;
  }

  switch (operator) {
    case "=":
      return activatorValue === comparedValue;

    case "!=":
      return activatorValue !== comparedValue;

    case "<":
      return activatorValue < comparedValue;

    case ">":
      return activatorValue > comparedValue;

    case "<=":
      return activatorValue <= comparedValue;

    case ">=":
      return activatorValue >= comparedValue;

    default:
      console.warn("unknown operator", operator);
      return false;
  }
}

export function getConditionActivatorValue(
  condition: ConditionItem,
  rounds: SimulationRoundItem[]
) {
  return getConditionPropertyValue(
    {
      type: condition.activatorType,
      id: condition.activatorId,
      property: condition.activatorProperty,
      propertyVariant: condition.activatorPropertyVariant,
      start: condition.activatorStart,
      startType: condition.activatorStartType,
      end: condition.activatorEnd,
      endType: condition.activatorEndType
    },
    rounds
  );
}

export function getConditionComparedValue(
  condition: ConditionItem,
  rounds: SimulationRoundItem[]
) {
  return getConditionPropertyValue(
    {
      type: condition.comparedType,
      valueType: condition.comparedValueType,
      value: condition.comparedValue,
      id: condition.comparedId,
      property: condition.comparedProperty,
      startType: condition.comparedStartType,
      start: condition.comparedStart,
      endType: condition.comparedEndType,
      end: condition.comparedEnd
    },
    rounds
  );
}

export function getConditionPropertyValue(
  item: {
    type: ConditionActivatorType | ConditionComparedType | null;
    id: ConditionTypeId<ConditionActivatorType>;
    property: ConditionItemProperty<ConditionActivatorType>;
    propertyVariant?: "positive" | "negative" | null;
    start?: number | null;
    startType?: ConditionItem["activatorStartType"];
    end?: number | null;
    endType?: ConditionItem["activatorEndType"];
    valueType?: "number" | "absoluteRound" | "relativeRound";
    value?: number | null;
  },
  rounds: SimulationRoundItem[]
): number | undefined {
  if (item.type == null) return undefined;

  const lastRound = rounds[rounds.length - 1];

  if (item.type == "value") {
    if (item.valueType == "number") return item.value || 0;
    if (item.valueType == "absoluteRound") return item.value || 0;
    if (item.valueType == "relativeRound") {
      return lastRound.index - (item.value || 0);
    }
  }

  const startRound = getRound(item.startType, item.start, rounds);
  let roundsInRange = getRoundsInRange(
    rounds,
    item.startType,
    item.start,
    item.endType,
    item.end
  );

  let round = lastRound;

  if (item.type === "round") {
    return round.index;
  }
  if (item.type === "roundRange") {
    return roundsInRange.includes(lastRound) ? 1 : undefined;
  }

  const timeForm = getConditionTimeForm(item.type, item.property);
  const usedTimeForm = getUsedConditionTimeForm(
    timeForm,
    item.startType,
    item.start,
    item.endType,
    item.end
  );

  // For single-round properties, if there's a round requested, make sure it exists.
  if (supportsTime(item.type, item.property) && usedTimeForm === "point") {
    if (item.startType) {
      if (startRound == null) return undefined;
      round = startRound;
    }
  }
  if (usedTimeForm == "point") {
    roundsInRange.length = 1;
    roundsInRange[0] = round;
  }

  if (item.id == null) return undefined;

  switch (item.type) {
    case "target": {
      const itemUID = toUid(item.type, item.id);
      switch (item.property) {
        case "value": {
          // Only the currentRound is not played.
          // But should be checked, for incidents!
          if (round.played == false) {
            const value = round.startElementValues[itemUID].value;
            if (value == null) return undefined;
            return Number(value);
          }
          const elementValue = round.elementValues[itemUID];
          if (elementValue == null) console.log("no element value", itemUID, item, round.elementValues);
          const value = elementValue?.value;
          if (value == null) return undefined;
          return Number(value);
        }
        case "valueChange":
        case "increase":
        case "decrease": {
          // Exclude any rounds that have not been played
          // because their value change will be 0 by default.
          const playedRoundsInRange = roundsInRange.filter((r) => r.played);
          if (playedRoundsInRange.length === 0) return undefined;

          let roundA = playedRoundsInRange[0];
          let roundB = playedRoundsInRange[playedRoundsInRange.length - 1];
          if (usedTimeForm == "point") {
            roundA = roundB = round;
          }

          const startValue = roundA.startElementValues[itemUID].value;
          const endValue = roundB.elementValues[itemUID].value;

          if (startValue == null || endValue == null) return 0;

          const change = Number(endValue) - Number(startValue);

          if (
            item.property === "increase" ||
            item.propertyVariant === "positive"
          ) {
            // Always return a positive value
            if (change > 0) {
              return change;
            }
            return 0;
          } else if (
            item.property === "decrease" ||
            item.propertyVariant === "negative"
          ) {
            // Always return a negative value
            if (change < 0) {
              return -change;
            }
            return 0;
          }
          return change;
        }
        default:
          return undefined;
      }

      break;
    }
    case "resource": {
      const itemUID = toUid(item.type, item.id);
      switch (item.property) {
        case "value": {
          if (round.played == false) {
            const value = round.startElementValues[itemUID].value;
            if (value == null) return undefined;
            return Number(value);
          }
          const value = round.elementValues[itemUID].value;
          if (value == null) return undefined;
          return Number(value);
        }
        case "valueChange":
        case "increase":
        case "decrease": {
          let roundA, roundB;
          if (usedTimeForm == "range") {
            if (roundsInRange.length === 0) return undefined;
            roundA = roundsInRange[0];
            roundB = roundsInRange[roundsInRange.length - 1];
          } else {
            roundA = roundB = round;
          }

          const startValue = roundA.startElementValues[itemUID].value;
          const endValue = roundB.elementValues[itemUID].value;

          if (startValue == null || endValue == null) return 0;

          const change = Number(endValue) - Number(startValue);

          // Always return a positive value
          if (
            item.property === "increase" ||
            item.propertyVariant === "positive"
          ) {
            if (change > 0) {
              return change;
            }
            return 0;
          } else if (
            item.property === "decrease" ||
            item.propertyVariant === "negative"
          ) {
            if (change < 0) {
              return -change;
            }
            return 0;
          }
          return change;
        }
        default:
          return undefined;
      }

      break;
    }
    case "index": {
      const itemUID = toUid(item.type, item.id);
      switch (item.property) {
        case "value": {
          if (round.played == false) {
            const value = round.startElementValues[itemUID]?.value;
            if (value == null) console.log("no value", itemUID, item, rounds);
            if (value == null) return undefined;
            return Number(value);
          }
          const value = round.elementValues[itemUID].value;
          if (value == null) return undefined;
          return Number(value);
        }
        case "valueChange":
        case "increase":
        case "decrease": {
          let roundA, roundB;
          if (usedTimeForm == "range") {
            if (roundsInRange.length === 0) return undefined;
            roundA = roundsInRange[0];
            roundB = roundsInRange[roundsInRange.length - 1];
          } else {
            roundA = roundB = round;
          }

          const startValue = roundA.startElementValues[itemUID].value;
          const endValue = roundB.elementValues[itemUID].value;
          if (startValue == null || endValue == null) return 0;

          const change = Number(endValue) - Number(startValue);

          // Always return a positive value
          if (
            item.property === "increase" ||
            item.propertyVariant === "positive"
          ) {
            if (change > 0) {
              return change;
            }
            return 0;
          } else if (
            item.property === "decrease" ||
            item.propertyVariant === "negative"
          ) {
            if (change < 0) {
              return -change;
            }
            return 0;
          }
          return change;
        }
        default:
          return undefined;
      }

      break;
    }
    case "action": {
      switch (item.property) {
        case "activationCount": {
          if (usedTimeForm == "point") {
            return round.activatedActions.find(
              (i) => i.actionId == item.id
            ) != null
              ? 1
              : 0;
          } else {
            // get the sum of all action plays
            // in the checked rounds
            const activationCount = roundsInRange.reduce(
              (sum, r) =>
                sum +
                (r.activatedActions.filter((m) => m.actionId === item.id)
                  .length || 0),
              0
            );
            return activationCount;
          }
        }
        case "activationTime": {
          // get the index of the last round where the action was activated
          const activationRound = rounds
            .slice()
            .reverse()
            .find((r) =>
              r.activatedActions.find((m) => m.actionId === item.id)
            );
          if (activationRound == null) return undefined;

          return activationRound.index;
        }
        default:
          return undefined;
      }

      break;
    }
    case "incident": {
      // get the rounds set by the range

      switch (item.property) {
        case "activationCount": {
          if (usedTimeForm == "point") {
            return round.activatedIncidents.find(
              (i) => i.incidentId == item.id
            ) != null
              ? 1
              : 0;
          } else {
            // get the sum of all incident activations
            // in the checked rounds
            const activations = roundsInRange.reduce(
              (sum, r) =>
                sum +
                (r.activatedIncidents.filter((m) => m.incidentId === item.id)
                  .length || 0),
              0
            );
            return activations;
          }
        }
        case "activationTime": {
          // get the index of the last round where the action was activated
          const activationRound = rounds
            .slice()
            .reverse()
            .find((r) =>
              r.activatedIncidents.find((m) => m.incidentId === item.id)
            );
          if (activationRound == null) return undefined;

          return activationRound.index;
        }
        default:
          return undefined;
      }
      break;
    }
  }
}

export function getRound(
  type:
    | "absoluteRound"
    | "relativeRound"
    | "startRound"
    | "currentRound"
    | null
    | undefined,
  value: number | null | undefined,
  rounds: SimulationRoundItem[]
): SimulationRoundItem | null {
  if (type == null) return null;

  if (type === "absoluteRound") {
    if (value == null) return null;
    return rounds[value] || null;
  } else if (type == "relativeRound") {
    if (value == null) return null;
    const roundIndex = rounds.length - 1 - value;

    if (roundIndex < 0) {
      return {
        type: "simulationRound",
        index: roundIndex,
        played: true,
        elementValues: rounds[0].startElementValues,
        startElementValues: rounds[0].startElementValues,
        activatedActions: [],
        activatedIncidents: [],
        activatedMomentums: [],
        impulses: {
          target: {},
          resource: {}
        },
        incidentCounts: {},
        actionCounts: {},
        triggerCounts: {},
        pendingEffects: [],
        preventedActions: [],
        notPayableActions: [],
        startRandomValues: {},
        randomValues: {},
        id: -1,
        uid: "",
        createdAt: 0
      };
    }

    return rounds[roundIndex];
  } else if (type == "startRound") {
    return {
      type: "simulationRound",
      index: -1,
      played: true,
      elementValues: rounds[0].startElementValues,
      startElementValues: rounds[0].startElementValues,
      activatedActions: [],
      activatedIncidents: [],
      activatedMomentums: [],
      impulses: {
        target: {},
        resource: {}
      },
      incidentCounts: {},
      actionCounts: {},
      triggerCounts: {},
      pendingEffects: [],
      preventedActions: [],
      notPayableActions: [],
      randomValues: {},
      startRandomValues: {},
      id: -1,
      uid: "",
      createdAt: 0
    };
  } else if (type == "currentRound") {
    return rounds[rounds.length - 1];
  }

  console.warn("unknown type:", type);

  throw new Error("Unknown type");
}

export function getRoundIndex(
  type:
    | "absoluteRound"
    | "relativeRound"
    | "startRound"
    | "currentRound"
    | null
    | undefined,
  value: number | null | undefined,
  rounds: SimulationRoundItem[]
): number | null {
  if (type == null) return null;

  if (type === "absoluteRound") {
    if (value == null) return null;
    return value;
  } else if (type == "relativeRound") {
    if (value == null) return null;
    return rounds.length - 1 - value;
  } else if (type == "startRound") {
    return 0;
  } else if (type == "currentRound") {
    return rounds.length - 1;
  }

  throw new Error("Unknown type");
}

function getRoundsInRange(
  rounds: SimulationRoundItem[],
  startType?: ConditionItem["activatorStartType"],
  start?: number | null | undefined,
  endType?: ConditionItem["activatorEndType"],
  end?: number | null | undefined
): SimulationRoundItem[] {
  if (startType == null) return rounds.slice();
  if (endType == null) return rounds.slice();

  const roundIndex = rounds[rounds.length - 1].index;
  let startRoundIndex: number | null = null;
  let endRoundIndex: number | null = null;

  if (startType == "absoluteRound") {
    if (start == null) return [];
    startRoundIndex = start;
  } else if (startType == "relativeRound") {
    if (start == null) return [];
    startRoundIndex = roundIndex - start;
  } else if (startType == "startRound") {
    startRoundIndex = 0;
  } else if (startType == "currentRound") {
    startRoundIndex = roundIndex;
  }
  if (startRoundIndex == null) return [];

  if (endType == "absoluteRound") {
    if (end == null) return [];
    endRoundIndex = end;
  } else if (endType == "relativeRound") {
    if (end == null) return [];
    endRoundIndex = roundIndex - end;
  } else if (endType == "currentRound") {
    endRoundIndex = roundIndex;
  }
  if (endRoundIndex == null) return [];

  if (startRoundIndex < 0) startRoundIndex = 0;
  if (startRoundIndex > endRoundIndex) return [];
  if (startRoundIndex > roundIndex) return [];
  if (endRoundIndex > roundIndex) endRoundIndex = roundIndex;

  const rangeRounds = rounds.slice(startRoundIndex, endRoundIndex + 1);

  return rangeRounds;
}

export function getValidOperators(
  condition: ConditionItem
): ConditionOperator[] {
  if (condition.activatorType == "round") {
    return ["=", "!=", ">=", "<=", ">", "<"];
  }
  if (condition.activatorType == "roundRange") {
    return ["?", "!"];
  }
  if (condition.activatorType == null) {
    return [];
  }
  if (condition.activatorProperty == "value") {
    return ["=", "!=", ">=", "<=", ">", "<"];
  }
  if (condition.activatorProperty == "activationCount") {
    if (
      condition.activatorStartType != null &&
      condition.activatorEndType == null
    ) {
      return ["?", "!"];
    }
    return ["=", "!=", ">=", "<=", ">", "<", "?", "!"];
  }
  return ["=", "!=", ">=", "<=", ">", "<", "?", "!"];
}

export function getConditionTimeForm(
  type: ConditionActivatorType | ConditionComparedType | null,
  property: ConditionItemProperty<ConditionActivatorType>
) {
  if (type == "roundRange") return "range";

  if (
    property == "activationCount" ||
    property == "valueChange" ||
    property == "increase" ||
    property == "decrease"
  ) {
    return "range";
  }
  if (property == "value" || property == "activationTime") {
    return "point";
  }
  return "point";
}

export function getUsedConditionTimeForm(
  timeForm: "point" | "range",
  startType?: ConditionItem["activatorStartType"],
  start?: ConditionItem["activatorStart"],
  endType?: ConditionItem["activatorEndType"],
  end?: ConditionItem["activatorEnd"]
) {
  if (timeForm == "point") return "point";

  if (endType == null) return "point";

  if (
    areStartAndEndDifferent(
      startType ?? null,
      start ?? null,
      endType ?? null,
      end ?? null
    )
  ) {
    return "range";
  }

  return "point";
}

function areStartAndEndDifferent(
  startType: string | null,
  start: number | null,
  endType: string | null,
  end: number | null
) {
  if (startType != endType) return true;
  if (start != end) return true;
  return false;
}

/**
 * Checks if all changes result in a valid condition
 * if not, the changing properties are edited.
 */
export function ensureConditionValidity(
  condition: ConditionItem,
  changingProperties: Partial<ConditionItem>
) {
  ensureValidActivatorPropertyVariant(condition, changingProperties);

  ensureValidOperator(condition, changingProperties);

  ensureValidActivatorStartType(condition, changingProperties);
  ensureValidActivatorStart(condition, changingProperties);
  ensureValidActivatorEndType(condition, changingProperties);

  ensureValidComparedProperty(condition, changingProperties);

  ensureValidComparedStartType(condition, changingProperties);
  ensureValidComparedEndType(condition, changingProperties);

  ensureValidComparedValue(condition, changingProperties);
  ensureValidComparedObject(condition, changingProperties);
}

export function ensureValidActivatorPropertyVariant(
  condition: ConditionItem,
  changes: Partial<ConditionItem>
) {
  const joinedCondition = {
    ...condition,
    ...changes
  };

  // Only the "valueChange" property supports variants
  if (joinedCondition.activatorPropertyVariant != null) {
    if (joinedCondition.activatorProperty != "valueChange") {
      changes.activatorPropertyVariant = null;
    }
  } else {
    // ...but there, it is required!
    if (joinedCondition.activatorProperty == "valueChange") {
      changes.activatorPropertyVariant = "negative";
    }
  }
}

/**
 * Checks if the operator is valid for the condition
 * if not, the operator will be changed.
 */
export function ensureValidOperator(
  condition: ConditionItem,
  changingProperties: Partial<ConditionItem>
) {
  const joinedCondition = {
    ...condition,
    ...changingProperties
  };
  const validOperators = getValidOperators(joinedCondition);

  if (validOperators.length === 0) {
    changingProperties.operator = null;
    return;
  }

  if (joinedCondition.operator == null) {
    changingProperties.operator = validOperators[0];
    return;
  }

  if (!validOperators.includes(joinedCondition.operator)) {
    changingProperties.operator = validOperators[0];
  }
}

export function ensureValidActivatorStartType(
  condition: ConditionItem,
  changes: Partial<ConditionItem>
) {
  const joinedCondition = {
    ...condition,
    ...changes
  };

  const validStartTypes = getValidActivatorStartTypes(joinedCondition);

  if (validStartTypes.length === 0) {
    changes.activatorStartType = null;
    return;
  }

  if (joinedCondition.activatorStartType == null) {
    changes.activatorStartType = validStartTypes[0];
    return;
  }

  if (!validStartTypes.includes(joinedCondition.activatorStartType)) {
    changes.activatorStartType = validStartTypes[0];
  }
}

export function ensureValidActivatorStart(
  condition: ConditionItem,
  changes: Partial<ConditionItem>
) {
  const joinedCondition = {
    ...condition,
    ...changes
  };

  if (
    joinedCondition.activatorStartType == null &&
    joinedCondition.activatorStart != null
  ) {
    changes.activatorStart = null;
    return;
  }

  if (joinedCondition.activatorStart == null) {
    if (joinedCondition.activatorStartType == "absoluteRound") {
      changes.activatorStart = 0;
    }
    if (joinedCondition.activatorStartType == "relativeRound") {
      changes.activatorStart = 1;
    }
  }

  return;
}

export function ensureValidActivatorEndType(
  condition: ConditionItem,
  changingProperties: Partial<ConditionItem>
) {
  const joinedCondition = {
    ...condition,
    ...changingProperties
  };

  const validEndTypes = getValidActivatorEndTypes(joinedCondition);

  if (validEndTypes.length === 0) {
    changingProperties.activatorEndType = null;
    return;
  }

  if (!validEndTypes.includes(joinedCondition.activatorEndType)) {
    changingProperties.activatorEndType = validEndTypes[0];
  }
}

export function ensureValidComparedProperty(
  condition: ConditionItem,
  changingProperties: Partial<ConditionItem>
) {
  const joinedCondition = {
    ...condition,
    ...changingProperties
  };

  const validComparedProperties = getValidComparedProperties(joinedCondition);

  if (validComparedProperties.length === 0) {
    changingProperties.comparedProperty = null;
    return;
  }

  if (joinedCondition.comparedProperty == null) {
    return;
  }

  if (!validComparedProperties.includes(joinedCondition.comparedProperty)) {
    changingProperties.comparedProperty = null;
  }
}

export function getValidComparedProperties(
  condition: ConditionItem
): ConditionItem["comparedProperty"][] {
  if (
    condition.activatorProperty == "activationTime" ||
    condition.activatorType == "round"
  ) {
    return ["activationTime"];
  }
  switch (condition.comparedType) {
    case "target":
    case "resource":
    case "index":
      return ["value", "increase", "decrease"]; // "valueChange"
    case "incident":
    case "action":
      return ["activationCount", "activationTime"];
    default:
      return [];
  }
}

export function getValidActivatorStartTypes(
  condition: ConditionItem
): ConditionItem["activatorStartType"][] {
  if (condition == null) return [];

  const timeForm = getConditionTimeForm(
    condition.activatorType,
    condition.activatorProperty
  );

  if (condition.activatorType == "roundRange") {
    return ["absoluteRound"];
  }

  if (timeForm == "point" || condition.activatorEndType == null) {
    if (condition.activatorProperty == "value") {
      return ["currentRound", "startRound", "absoluteRound", "relativeRound"];
    }
    return ["currentRound", "absoluteRound", "relativeRound"];
  }

  return ["startRound", "absoluteRound", "relativeRound"];
}

export function getValidActivatorEndTypes(
  condition: ConditionItem
): ConditionItem["activatorEndType"][] {
  if (condition == null) return [];

  if (condition.activatorType == "roundRange") {
    return ["absoluteRound"];
  }

  const timeForm = getConditionTimeForm(
    condition.activatorType,
    condition.activatorProperty
  );

  if (timeForm == "point") return [];

  return ["currentRound", "absoluteRound", "relativeRound", null];
}

function ensureValidComparedStartType(
  condition: ConditionItem,
  changingProperties: Partial<ConditionItem>
) {
  const joinedCondition = {
    ...condition,
    ...changingProperties
  };

  const validStartTypes = getValidComparedStartTypes(joinedCondition);

  if (validStartTypes.length === 0) {
    changingProperties.comparedStartType = null;
    return;
  }

  if (joinedCondition.comparedStartType == null) {
    changingProperties.comparedStartType = validStartTypes[0];
    return;
  }

  if (!validStartTypes.includes(joinedCondition.comparedStartType)) {
    changingProperties.comparedStartType = validStartTypes[0];
  }
}

function ensureValidComparedEndType(
  condition: ConditionItem,
  changingProperties: Partial<ConditionItem>
) {
  const joinedCondition = {
    ...condition,
    ...changingProperties
  };

  const validEndTypes = getValidComparedEndTypes(joinedCondition);

  if (validEndTypes.length === 0) {
    changingProperties.comparedEndType = null;
    return;
  }

  if (joinedCondition.comparedEndType == null) {
    return;
  }

  if (!validEndTypes.includes(joinedCondition.comparedEndType)) {
    changingProperties.comparedEndType = null;
  }
}

export function ensureValidComparedValue(
  condition: ConditionItem,
  changingProperties: Partial<ConditionItem>
) {
  const joinedCondition = {
    ...condition,
    ...changingProperties
  };

  if (joinedCondition.comparedValue == null) {
    return;
  }

  const settings = useSettingsStore();

  if (joinedCondition.activatorType == "round") {
    if (joinedCondition.comparedValue < 0) {
      changingProperties.comparedValue = 0;
    }
    const maxRound = settings.simulationRoundAmount - 1;
    if (joinedCondition.comparedValue > maxRound) {
      changingProperties.comparedValue = maxRound;
    }
  }
}

export function ensureValidComparedObject(
  condition: ConditionItem,
  changingProperties: Partial<ConditionItem>
) {
  const joinedCondition = {
    ...condition,
    ...changingProperties
  };

  if (joinedCondition.comparedType == null) {
    return;
  }

  const validComparedTypes = getValidComparedTypes(joinedCondition);

  if (!validComparedTypes.includes(joinedCondition.comparedType)) {
    if (validComparedTypes.length === 0) {
      changingProperties.comparedType = null;
    } else {
      changingProperties.comparedType = validComparedTypes[0];
    }
    changingProperties.comparedProperty = null;
    changingProperties.comparedId = null;
  }

  if (joinedCondition.comparedType == "value") {
    if (joinedCondition.comparedValueType == null) {
      changingProperties.comparedValueType = "number";
    }
  }
}

export function getValidComparedStartTypes(
  condition: ConditionItem
): ConditionItem["comparedStartType"][] {
  if (condition == null) return [];

  const timeForm = getConditionTimeForm(
    condition.comparedType,
    condition.comparedProperty
  );

  if (timeForm == "point" || condition.comparedEndType == null) {
    if (condition.comparedProperty == "value") {
      return ["currentRound", "startRound", "absoluteRound", "relativeRound"];
    }
    return ["currentRound", "absoluteRound", "relativeRound"];
  }

  return ["startRound", "absoluteRound", "relativeRound"];
}

export function getValidComparedEndTypes(
  condition: ConditionItem
): ConditionItem["comparedEndType"][] {
  if (condition == null) return [];

  const timeForm = getConditionTimeForm(
    condition.comparedType,
    condition.comparedProperty
  );

  if (timeForm == "point") return [];

  return ["currentRound", "absoluteRound", "relativeRound", null];
}

export function getValidComparedTypes(condition: ConditionItem) {
  const types: ConditionComparedType[] = [];
  if (condition.activatorType == "roundRange") {
    return types;
  }

  types.push("value");

  const features = useFeatures();
  if (
    condition.activatorType == "round" ||
    condition.activatorProperty == "activationTime"
  ) {
    if (features.incident) types.push("incident");
    if (features.action) types.push("action");
    return types;
  }

  if (features.target) types.push("target");
  if (features.resource) types.push("resource");
  if (features.index) types.push("index");
  if (features.incident) types.push("incident");
  if (features.action) types.push("action");
  return types;
}

export function supportsTime(
  type: ConditionItem["activatorType"] | ConditionItem["comparedType"],
  property: ConditionItem["activatorProperty"]
) {
  if (type == null) return false;

  if (type == "round") return false;
  if (type == "roundRange") return true;
  if (property == null) return false;
  if (property == "activationTime") return false;

  return true;
}

export function getInverseOperator(
  operator: ConditionItem["operator"]
): ConditionItem["operator"] {
  switch (operator) {
    case "=":
      return "!=";
    case "!=":
      return "=";
    case "<":
      return ">=";
    case "<=":
      return ">";
    case ">":
      return "<=";
    case ">=":
      return "<";
    case "?":
      return "!";
    case "!":
      return "?";
    default:
      return operator;
  }
}
